import Typo from 'typo-js';

import SPELL_CHECK_ALLOWLIST from './spellCheckAllowlist';

const SUGGESTION_LIMIT = 5;
const MAX_TYPO_CHECK_CACHE_SIZE = 10000;
const MAX_TYPO_SUGGEST_CACHE_SIZE = 10000;
const CUSTOM_ALLOWLIST = new Set(SPELL_CHECK_ALLOWLIST);

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz';

/**
 * Generate all words that are 1 edit away from the input word.
 * Edits: deletes, transposes, replaces, inserts.
 * Using Norman's algorithm
 * https://norvig.com/spell-correct.html
 */
const generateWordsThatAreOneEditAway = (word: string): Set<string> => {
  const results = new Set<string>();
  const lower = word.toLowerCase();

  for (let i = 0; i < lower.length; i++) {
    results.add(lower.slice(0, i) + lower.slice(i + 1));

    if (i < lower.length - 1) {
      results.add(
        lower.slice(0, i) + lower[i + 1] + lower[i] + lower.slice(i + 2),
      );
    }
    for (const c of ALPHABET) {
      if (c !== lower[i]) {
        results.add(lower.slice(0, i) + c + lower.slice(i + 1));
      }
    }
    for (const c of ALPHABET) {
      results.add(lower.slice(0, i) + c + lower.slice(i));
    }
  }
  for (const c of ALPHABET) {
    results.add(lower + c);
  }

  return results;
};

const scoreSuggestion = (candidate: string, original: string): number => {
  const lc = candidate.toLowerCase();
  const lo = original.toLowerCase();
  let score = 0;

  let prefixLen = 0;
  for (let i = 0; i < Math.min(lc.length, lo.length); i++) {
    if (lc[i] === lo[i]) {
      prefixLen++;
    } else {
      break;
    }
  }
  score += prefixLen * 5;

  if (lc[0] === lo[0]) {
    score += 10;
  }

  score -= Math.abs(lc.length - lo.length) * 3;

  score += Math.min(lc.length, 8);

  const originalChars = new Map<string, number>();
  for (const c of lo) originalChars.set(c, (originalChars.get(c) ?? 0) + 1);
  const candidateChars = new Map<string, number>();
  for (const c of lc) candidateChars.set(c, (candidateChars.get(c) ?? 0) + 1);
  let sharedChars = 0;
  for (const [c, count] of originalChars) {
    sharedChars += Math.min(count, candidateChars.get(c) ?? 0);
  }
  score += sharedChars * 6;

  return score;
};

const editDistanceSuggestions = (typo: Typo, word: string): string[] => {
  const firstEdit = generateWordsThatAreOneEditAway(word);
  const firstDistance: string[] = [];

  const MAX_SECOND_DISTANCE_CHECKS = 5000;
  const secondDistance: string[] = [];
  const seen = new Set(firstDistance);
  let secondDistanceChecks = 0;

  for (const candidate of firstEdit) {
    if (candidate.length >= 2 && typo.check(candidate)) {
      firstDistance.push(candidate);
    }
  }

  if (firstDistance.length >= SUGGESTION_LIMIT) {
    firstDistance.sort(
      (a, b) => scoreSuggestion(b, word) - scoreSuggestion(a, word),
    );
    return firstDistance;
  }

  for (const firstEditWord of firstEdit) {
    if (
      firstDistance.length + secondDistance.length >= SUGGESTION_LIMIT * 3 ||
      secondDistanceChecks >= MAX_SECOND_DISTANCE_CHECKS
    )
      break;

    for (const candidate of generateWordsThatAreOneEditAway(firstEditWord)) {
      secondDistanceChecks++;
      if (secondDistanceChecks >= MAX_SECOND_DISTANCE_CHECKS) break;

      if (
        candidate.length >= 2 &&
        !seen.has(candidate) &&
        typo.check(candidate)
      ) {
        seen.add(candidate);
        secondDistance.push(candidate);
      }
    }
  }

  firstDistance.sort(
    (a, b) => scoreSuggestion(b, word) - scoreSuggestion(a, word),
  );
  secondDistance.sort(
    (a, b) => scoreSuggestion(b, word) - scoreSuggestion(a, word),
  );

  return [...firstDistance, ...secondDistance];
};

let typoInstance: Typo | null = null;
let typoPromise: Promise<Typo> | null = null;

const getTypoInstance = (): Promise<Typo> => {
  if (typoInstance) {
    return Promise.resolve(typoInstance);
  }
  if (typoPromise) {
    return typoPromise;
  }
  typoPromise = Promise.all([
    fetch('/dictionaries/en_US.aff').then((res) => {
      if (!res.ok) throw new Error(`Failed to load aff: ${res.status}`);
      return res.text();
    }),
    fetch('/dictionaries/en_US.dic').then((res) => {
      if (!res.ok) throw new Error(`Failed to load dic: ${res.status}`);
      return res.text();
    }),
  ])
    .then(([aff, dic]) => {
      typoInstance = new Typo('en_US', aff, dic);
      return typoInstance;
    })
    .catch((error: unknown) => {
      typoPromise = null;
      throw error;
    });

  return typoPromise;
};

const getTypoInstanceSync = (): Typo | null => typoInstance;

const checkCache = new Map<string, boolean>();

const cachedCheck = (typo: Typo, word: string): boolean => {
  const cached = checkCache.get(word);
  if (cached !== undefined) {
    return cached;
  }
  if (checkCache.size >= MAX_TYPO_CHECK_CACHE_SIZE) {
    checkCache.clear();
  }
  const result = typo.check(word);
  checkCache.set(word, result);
  return result;
};

const suggestCache = new Map<string, string[]>();

const cachedSuggest = (
  typo: Typo,
  word: string,
  limit = SUGGESTION_LIMIT,
): string[] => {
  const cached = suggestCache.get(word);
  if (cached !== undefined) {
    return cached;
  }

  if (suggestCache.size >= MAX_TYPO_SUGGEST_CACHE_SIZE) {
    suggestCache.clear();
  }

  const editCandidates = editDistanceSuggestions(typo, word);
  const nativeSuggestions = typo.suggest(word);

  const seen = new Set<string>();
  const merged: string[] = [];

  for (const s of editCandidates) {
    const lower = s.toLowerCase();
    if (!seen.has(lower)) {
      seen.add(lower);
      merged.push(s);
    }
  }

  for (const s of nativeSuggestions) {
    const lower = s.toLowerCase();
    if (!seen.has(lower)) {
      seen.add(lower);
      merged.push(s);
    }
  }

  const results = merged.slice(0, limit);
  suggestCache.set(word, results);
  return results;
};

const prefetchSuggestions = (typo: Typo, word: string): void => {
  if (suggestCache.has(word)) {
    return;
  }
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    requestIdleCallback(() => cachedSuggest(typo, word));
  }
};

export {
  getTypoInstance,
  getTypoInstanceSync,
  cachedCheck,
  cachedSuggest,
  prefetchSuggestions,
  CUSTOM_ALLOWLIST,
};
